import qs from 'qs';
import { ResponseError } from '@backstage/errors';
import type { DiscoveryApi, FetchApi } from '@backstage/core-plugin-api';
import type { SearchApi } from '@backstage/plugin-search-react';
import type { SearchQuery, SearchResultSet } from '@backstage/plugin-search-common';

const EMPTY_RESULTS: SearchResultSet = { results: [], numberOfResults: 0 };

/**
 * Same request the vendor `SearchClient` makes, except a `MissingIndexError` —
 * a collator that has not yet indexed a single document, e.g. announcements
 * with zero rows in local dev — resolves to zero results instead of throwing.
 */
export class ResilientSearchApi implements SearchApi {
  constructor(
    private readonly discoveryApi: DiscoveryApi,
    private readonly fetchApi: FetchApi,
  ) {}

  async query(
    query: SearchQuery,
    options?: { signal?: AbortSignal },
  ): Promise<SearchResultSet> {
    const queryString = qs.stringify(query);
    const baseUrl = await this.discoveryApi.getBaseUrl('search');
    const response = await this.fetchApi.fetch(
      `${baseUrl}/query?${queryString}`,
      { signal: options?.signal },
    );
    if (response.ok) {
      return response.json();
    }

    const error = await ResponseError.fromResponse(response);
    if (error.cause?.name === 'MissingIndexError') {
      return EMPTY_RESULTS;
    }
    throw error;
  }
}
